import type { one_to_one } from '@fireworkhq/proto-contract'
import type {
  UIEvent as UIEvent,
  UIState as UIState,
} from '@liveHelper/models/fsm/machine'
import { uiMachine } from '@liveHelper/models/fsm/machine'
import type { createSlice as createOneToOneModalSlice } from '@liveHelper/models/modal/modalRegistry'
import { getCall } from '@liveHelper/models/persistence/call'
import type { LiveHelperParams } from '@liveHelper/params'
import { TRANSFERRING_MESSAGE_REF } from '@oneToOne/shared/components/chat/constants'
import type { MessageAttachmentUploadStatus } from '@oneToOne/shared/components/chat/types'
import type { OneToOneSharedState } from '@oneToOne/shared/models/store/libs/actions'
import { sharedOneToOneActions } from '@oneToOne/shared/models/store/libs/actions'
import type { ClientRole, UID } from 'agora-rtc-sdk-ng'

import { uuidv4 } from '@core/helpers/uuidv4'
import type { GetState, NamedSet, UseBoundStore } from '@core/zustand'

export type StoreActions = ReturnType<typeof actions>

export interface AutomatedMessage extends one_to_one.visitor.AutomatedMessage {
  src: 'chat_channel'
}

type LiveHelperCoreState = {
  embedInstanceId: string
  actions: StoreActions
  visitorPrivateActions: LiveHelperVisitorPrivateActions
  visitorInformation: { firstName: string; email: string } | undefined
  params: LiveHelperParams
  isMonitoring: boolean
  isWidgetVisible: boolean
  visibilityRulesMatch: boolean
  waitlistCapacityReached: boolean
  callData:
    | {
        id: string
        agentId?: string
        channel?: string
        token?: string
        uid?: UID
      }
    | undefined
  clientRole: ClientRole
  uiState: UIState
  monitoringActions: LiveHelperMonitoringActions
  conversation: one_to_one.shared.Conversation | undefined
  previousConversation: one_to_one.shared.Conversation | undefined
  queuedMessages: one_to_one.shared.Message[]
  liveHelperErrors: Array<{ id: string; content: string }>
  queuePosition: undefined | number
  displayRecordingStatement: boolean
  redirectUrl: string
  isIdleStateTextClosedAutomatically: boolean
  idleStateTextDismissTime: number | undefined
  cameraAccessAllowedForVideoCallId: string | undefined
  automatedMessage: AutomatedMessage | undefined
  automatedMessageDismissTime: number | undefined
  closeWidgetTime: number | undefined
  isTransferring: boolean
} & OneToOneSharedState

export const initialState: LiveHelperInitialState = {
  embedInstanceId: uuidv4(),
  params: {},
  isMonitoring: false,
  isWidgetVisible: false,
  visibilityRulesMatch: false,
  waitlistCapacityReached: false,
  clientRole: 'audience',
  clientControls: {
    cameraEnabled: false,
    microphoneMuted: false,
  },
  uiState: uiMachine.initialState,
  visitorPrivateActions: {},
  visitorInformation: undefined,
  monitoringActions: {},
  messages: [],
  nextMessagesCursor: null,
  queuedMessages: [],
  liveHelperErrors: [],
  conversation: undefined,
  previousConversation: undefined,
  callData: undefined,
  isTyping: false,
  queuePosition: undefined,
  fullScreenPreview: undefined,
  displayRecordingStatement: true,
  redirectUrl: '',
  isIdleStateTextClosedAutomatically: false,
  idleStateTextDismissTime: undefined,
  automatedMessage: undefined,
  automatedMessageDismissTime: undefined,
  closeWidgetTime: undefined,
  cameraAccessAllowedForVideoCallId: undefined,
  isTransferring: false,
}

export interface TerminateCallProps {
  agentId?: string
  type?: 'stopDialing' | 'declineIncomingCall' | 'endOngoingCall'
}

export interface CallAgentProps {
  isTransferredConversation?: boolean
}

export interface LiveHelperVisitorPrivateActions {
  callAgent?: (callAgentProps?: CallAgentProps) => Promise<void>
  chatWithAgent?: () => Promise<void>
  endConversation?: () => void
  terminateCall?: ({ agentId, type }: TerminateCallProps) => Promise<void>
  takeCall?: (agentId?: string) => Promise<void>
  updateActiveStatus?: (isActive: boolean) => void
  sendTypingActivity?: () => void
  sendTextMessage?: (text: string, isConversationAction?: boolean) => void
  sendMediaMessage?: (message: one_to_one.shared.Message) => void
  updateMediaMessage?: (
    message: one_to_one.shared.Message,
    uploadStatus: MessageAttachmentUploadStatus,
    progress?: number,
  ) => void
  sendQueuedMessage?: (
    message: one_to_one.shared.Message,
    conversationId: string,
  ) => void
  sendMicrophoneStatus?: ({ isMuted }: { isMuted: boolean }) => void
  updatePrivacyConfiguration?: (
    permission: one_to_one.enums.PrivacyConfiguration,
  ) => void
  setCameraAccessAllowedForVideoCallId?: (videoCallId: string) => void
  enableCamera?: () => void
}

export interface LiveHelperMonitoringActions {
  takeFullSnapshot?: () => void
}

export type LiveHelperState = LiveHelperCoreState &
  ReturnType<typeof createOneToOneModalSlice>

export type LiveHelperInitialState = Omit<LiveHelperCoreState, 'actions'>

export type StateCreator = (
  set: NamedSet<LiveHelperState>,
  get: GetState<LiveHelperState>,
) => LiveHelperState

export type LiveHelperStore = UseBoundStore<LiveHelperState>

/**
 * Creates a set of actions for the store.
 */
export const actions = (
  set: NamedSet<LiveHelperState>,
  get: GetState<LiveHelperState>,
) => ({
  resetState: () => {
    set(
      (state) => {
        return {
          ...initialState,
          // Params should be preserved
          params: state.params,
        }
      },
      false,
      'resetState',
    )
  },
  setIsMonitoring: (isMonitoring: boolean) => {
    set(
      () => {
        return { isMonitoring }
      },
      false,
      'setIsMonitoring',
    )
  },
  setCallId: (callId: string, isAppointment?: boolean) => {
    const lastCallId = getCall()
    const isNewCall = callId !== lastCallId
    set(
      (prevState) => ({
        callData: {
          ...prevState.callData,
          id: callId,
        },
        clientControls: isNewCall
          ? {
              cameraEnabled: isAppointment
                ? prevState.clientControls.cameraEnabled
                : false,
              microphoneMuted: false,
            }
          : prevState.clientControls,
        displayRecordingStatement: isNewCall
          ? true
          : prevState.displayRecordingStatement,
      }),
      false,
      'setCallId',
    )
  },
  establishCall: (
    callData: LiveHelperState['callData'],
    clientRole: ClientRole,
  ) => {
    const lastCallId = getCall()
    const isNewCall = callData?.id !== lastCallId
    set(
      (prevState) => ({
        callData,
        clientRole,
        clientControls: isNewCall
          ? {
              cameraEnabled: false,
              microphoneMuted: false,
            }
          : prevState.clientControls,
        displayRecordingStatement: isNewCall
          ? true
          : prevState.displayRecordingStatement,
      }),
      false,
      'establishCall',
    )
  },
  setClientRole: (clientRole: ClientRole) => {
    set({ clientRole }, false, 'setClientRole')
  },
  terminateCall: () => {
    set(
      ({ messages }) => ({
        callData: undefined,
        clientRole: 'audience',
        clientControls: {
          cameraEnabled: false,
          microphoneMuted: false,
        },
        displayRecordingStatement: true,
        messages: messages.filter(
          (message) => message.type !== 'request_visitor_camera_access',
        ),
        cameraAccessAllowedForVideoCallId: undefined,
      }),
      false,
      'terminateCall',
    )
  },
  sendUIStateTransitionEvent: (event: UIEvent['type'] | UIEvent) => {
    set(
      ({ uiState }) => {
        const uiEvent = typeof event !== 'string' ? event : { type: event }
        return {
          uiState: uiMachine.transition(uiState, uiEvent),
        }
      },
      false,
      'sendUIStateTransitionEvent',
    )
  },
  setVisitorPrivateActions: (actions: LiveHelperVisitorPrivateActions) => {
    set(
      () => {
        return { visitorPrivateActions: actions }
      },
      false,
      'setVisitorPrivateActions',
    )
  },
  removeVisitorPrivateActions: () => {
    set(
      () => {
        return { visitorPrivateActions: {} }
      },
      false,
      'removeVisitorPrivateActions',
    )
  },
  setMonitoringActions: (actions: LiveHelperMonitoringActions) => {
    set({ monitoringActions: actions }, false, 'setMonitoringActions')
  },
  removeMonitoringActions: () => {
    set({ monitoringActions: {} }, false, 'removeMonitoringActions')
  },
  setWidgetState: ({
    isWidgetVisible,
    visibilityRulesMatch,
    waitlistCapacityReached,
    automatedMessage,
  }: {
    isWidgetVisible: LiveHelperCoreState['isWidgetVisible']
    visibilityRulesMatch: LiveHelperCoreState['visibilityRulesMatch']
    waitlistCapacityReached: LiveHelperCoreState['waitlistCapacityReached']
    automatedMessage?: LiveHelperCoreState['automatedMessage']
  }) => {
    set(
      (previousState) => ({
        isWidgetVisible,
        visibilityRulesMatch,
        waitlistCapacityReached,
        automatedMessage:
          previousState.automatedMessage?.src === automatedMessage?.src
            ? previousState.automatedMessage
            : automatedMessage,
      }),
      false,
      'setWidgetState',
    )
  },
  showWidget: () => {
    set(
      () => ({
        isWidgetVisible: true,
      }),
      false,
      'showWidget',
    )
  },
  hideWidget: () => {
    set(
      () => ({
        isWidgetVisible: false,
        uiState: uiMachine.initialState,
      }),
      false,
      'hideWidget',
    )
  },
  closeWidget: () => {
    const { hideWidget } = actions(set, get)
    hideWidget()

    set(
      () => ({
        closeWidgetTime: Date.now(),
      }),
      false,
      'closeWidgetTime',
    )
  },
  clearCloseWidgetTime: () => {
    set(
      () => ({
        closeWidgetTime: undefined,
      }),
      false,
      'clearCloseWidgetTime',
    )
  },
  setConversation: (conversation: one_to_one.shared.Conversation) => {
    const { queuedMessages, visitorPrivateActions, messages } = get()
    queuedMessages.forEach((message) => {
      visitorPrivateActions.sendQueuedMessage?.(message, conversation.id ?? '')
    })

    set(
      (prevState) => ({
        conversation,
        previousConversation:
          prevState.conversation ?? prevState.previousConversation,
        queuedMessages: [],
        automatedMessage: undefined,
        isTransferring: !!conversation?.transfer_details,
        messages: messages.filter(
          (message) => message.client_msg_ref !== TRANSFERRING_MESSAGE_REF,
        ),
      }),
      false,
      'setConversation',
    )
  },
  removeConversation: () => {
    set(
      (prevState) => ({
        conversation: undefined,
        previousConversation: prevState.conversation,
      }),
      false,
      'removeConversation',
    )
  },
  setConversationSuveyClosed: () => {
    // Use `previousConversation.agent_id` as a state to indicate
    // whether NPS has been closed (or submitted),
    //  - `undefined` means NPS has been closed (or submitted)
    // As the `previousConversation` itself is NOT cleared and levelaged in `AutomatedMessageProvider`
    set(
      (prevState) => ({
        previousConversation: {
          ...prevState.previousConversation,
          agent_id: undefined,
        },
      }),
      false,
      'setConversationSuveyClosed',
    )
  },
  addLiveHelperError: (content: { id: string; content: string }) => {
    set(
      ({ liveHelperErrors }) => ({
        liveHelperErrors: [...liveHelperErrors, content],
      }),
      false,
      'addLiveHelperError',
    )
  },
  removeLiveHelperError: (id: string) => {
    set(
      ({ liveHelperErrors }) => ({
        liveHelperErrors: liveHelperErrors.filter(
          (message) => message.id !== id,
        ),
      }),
      false,
      'removeLiveHelperError',
    )
  },
  queueMessage: (message: one_to_one.shared.Message) => {
    set(
      (prevState) => ({
        queuedMessages: [...prevState.queuedMessages, message],
      }),
      false,
      'queueTextMessage',
    )
  },
  setQueuePosition: (queuePosition: undefined | number) => {
    set(
      (previousState) => ({
        queuePosition: previousState.conversation?.agent
          ? undefined
          : queuePosition,
      }),
      false,
      'setQueuePosition',
    )
  },
  hideRecordingStatement: () => {
    set(
      () => ({
        displayRecordingStatement: false,
      }),
      false,
      'hideReordingStatement',
    )
  },
  setIdleStateTextClosedAutomatically: (value: boolean) => {
    set(
      () => ({
        isIdleStateTextClosedAutomatically: value,
      }),
      false,
      'setIdleStateTextClosedAutomatically',
    )
  },
  dismissIdleStateText: () => {
    set(
      () => ({
        idleStateTextDismissTime: Date.now(),
      }),
      false,
      'idleStateTextDismissTime',
    )
  },
  dismissAutomatedMessage: () => {
    set(
      () => ({
        automatedMessage: undefined,
        automatedMessageDismissTime: Date.now(),
      }),
      false,
      'dismissAutomatedMessage',
    )
  },
  clearAutomatedMessageDelay: () => {
    set(
      (previousState) => {
        const automatedMessage = previousState.automatedMessage?.message
          ? { ...previousState.automatedMessage, delay: 0 }
          : undefined

        return {
          automatedMessage,
        }
      },
      false,
      'clearAutomatedMessageDelay',
    )
  },
  setRedirectUrl: (redirectUrl: string) => {
    set(
      () => ({
        redirectUrl,
      }),
      false,
      'setRedirectUrl',
    )
  },
  setVisitorInformation: (
    visitorInformation: LiveHelperState['visitorInformation'],
  ) => {
    set(
      () => ({
        visitorInformation,
      }),
      false,
      'setVisitorInformation',
    )
  },
  addChatActions: (conversationId: string) => {
    set(
      ({ messages }) => {
        return {
          messages: [
            ...messages,
            {
              client_msg_ref: uuidv4(),
              content: '',
              conversation_id: conversationId,
              created_at: Date.now().toString(),
              name: '',
              type: 'post_sales_actions',
            },
          ],
        }
      },
      false,
      'addChatActions',
    )
  },
  removeLastChatActions: () => {
    set(
      (prevState) => {
        const newMessages = [...prevState.messages]
        for (let i = newMessages.length - 1; i >= 0; i--) {
          if (newMessages[i].type === 'post_sales_actions') {
            newMessages.splice(i, 1)
            break
          }
        }

        return {
          messages: newMessages,
        }
      },
      false,
      'removeLastChatActions',
    )
  },
  setCameraAccessAllowedForVideoCallId: (videoCallId: string | undefined) => {
    set(
      ({ messages, conversation }) => ({
        cameraAccessAllowedForVideoCallId: videoCallId,
        messages: [
          ...messages,
          {
            client_msg_ref: uuidv4(),
            content: '',
            conversation_id: conversation?.id,
            created_at: new Date().toISOString(),
            name: '',
            type: 'request_visitor_camera_access',
          },
        ],
      }),
      false,
      'setCameraAccessAllowedForVideoCallId',
    )
  },
  declineCameraInvite: () => {
    ;(set(({ clientControls }) => ({
      clientControls: {
        ...clientControls,
        cameraEnabled: false,
      },
      cameraAccessAllowedForVideoCallId: undefined,
    })),
      false,
      'declineCameraInvite')
  },
  acceptCameraInvite: () => {
    set(
      ({ clientControls, cameraAccessAllowedForVideoCallId, callData }) => {
        if (cameraAccessAllowedForVideoCallId === callData?.id) {
          return {
            clientControls: {
              ...clientControls,
              cameraEnabled: true,
            },
          }
        }
        return {
          clientControls,
        }
      },
      false,
      'enableCamera',
    )
  },
  setIsTransferring: (isTransferring: boolean) =>
    set({ isTransferring }, false, 'setIsTransfered'),
  ...sharedOneToOneActions(set, get),
})
